import { useEffect, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Activity, AlertCircle, Save, Settings as SettingsIcon } from 'lucide-react';
import type { SettingItem } from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import { roleLabelTh } from '@/lib/role-label';
import { settingsListQueryOptions, useUpdateSetting } from '@/lib/settings';
import { rolesListQueryOptions } from '@/lib/users';

interface SettingsSearch {
  q?: string;
}

export const Route = createFileRoute('/_authenticated/settings/')({
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  component: SettingsPage,
});

const GROUP_LABELS: Record<string, string> = {
  site: 'เว็บไซต์',
  seo: 'SEO',
  comments: 'ความคิดเห็น',
  posts: 'บทความ',
  media: 'สื่อ',
  email: 'อีเมล',
  auth: 'ความปลอดภัย',
};

type ValueKind = 'string' | 'number' | 'boolean' | 'json';

function inferKind(value: unknown): ValueKind {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') return 'string';
  return 'json'; // array, object, null → edit as JSON text
}

function groupOf(key: string): string {
  const dot = key.indexOf('.');
  return dot > 0 ? key.slice(0, dot) : 'other';
}

function SettingsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const query = useQuery(settingsListQueryOptions());
  const canEdit = hasPermission(user, 'settings.update');
  const canReadAudit = hasPermission(user, 'audit.read');
  const allItems = query.data ?? [];
  const [searchInput, setSearchInput] = useState(search.q ?? '');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void navigate({ search: { q: searchInput.trim() || undefined } });
  };

  // Client-side filter — settings list is small (<50 keys typically)
  // and already loaded, so a round-trip would just add latency. Match
  // key OR description (case-insensitive) so users can search by what
  // they remember (the human label or the dot-prefixed key).
  const qLower = search.q?.toLowerCase() ?? '';
  const items = qLower
    ? allItems.filter(
        (i) =>
          i.key.toLowerCase().includes(qLower) ||
          (i.description?.toLowerCase().includes(qLower) ?? false),
      )
    : allItems;

  // Group items by prefix
  const grouped = items.reduce<Record<string, SettingItem[]>>((acc, item) => {
    const g = groupOf(item.key);
    if (!acc[g]) acc[g] = [];
    acc[g].push(item);
    return acc;
  }, {});

  const groupKeys = Object.keys(grouped).sort();

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
            ตั้งค่าระบบ
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {canEdit ? 'แก้ค่าเพื่อปรับการทำงานของระบบ' : 'อ่านอย่างเดียว (ไม่มีสิทธิ์แก้ไข)'}
          </p>
        </div>

        {/* Search — useful once the settings list grows past one
            screen. Matches on both key (for users who remember the
            dot-prefixed name) and description (for human labels). */}
        {allItems.length > 0 && (
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="ค้นหา key หรือคำอธิบาย..."
              className="max-w-md"
            />
            <Button type="submit" variant="outline">
              ค้นหา
            </Button>
            {search.q && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setSearchInput('');
                  void navigate({ search: {} });
                }}
              >
                ล้าง
              </Button>
            )}
          </form>
        )}

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <SettingsIcon className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">
              {search.q ? `ไม่พบ settings ที่ตรงกับ "${search.q}"` : 'ยังไม่มี settings'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {groupKeys.map((g) => (
              <Card key={g}>
                <CardHeader>
                  <CardTitle className="text-base">{GROUP_LABELS[g] ?? g}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {grouped[g]!.map((item) => (
                    <SettingRow
                      key={item.key}
                      item={item}
                      canEdit={canEdit}
                      canReadAudit={canReadAudit}
                    />
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface SettingRowProps {
  item: SettingItem;
  canEdit: boolean;
  canReadAudit: boolean;
}

function SettingRow({ item, canEdit, canReadAudit }: SettingRowProps) {
  // Key-specific override: auth.required_2fa_roles is a JSON array of
  // role slugs, but typing slugs into a JSON textarea means the admin
  // has to know "super_admin" / "editor" / etc. by heart and not
  // typo them — easy to silently break enforcement. Render a checkbox
  // list of actual roles instead. Other JSON-array settings
  // (e.g. media.allowed_types) keep the generic textarea since their
  // values are MIME strings the admin types from spec, not enums.
  if (item.key === 'auth.required_2fa_roles') {
    return <Required2faRolesRow item={item} canEdit={canEdit} canReadAudit={canReadAudit} />;
  }
  const update = useUpdateSetting();
  const kind = inferKind(item.value);

  // Local draft so user can edit freely before saving.
  // For JSON kind we keep a string draft + parse error state.
  const [draftString, setDraftString] = useState('');
  const [draftNumber, setDraftNumber] = useState(0);
  const [draftBoolean, setDraftBoolean] = useState(false);
  const [draftJson, setDraftJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Sync local state when remote value lands or changes.
  useEffect(() => {
    if (kind === 'string') setDraftString((item.value as string) ?? '');
    else if (kind === 'number') setDraftNumber((item.value as number) ?? 0);
    else if (kind === 'boolean') setDraftBoolean(Boolean(item.value));
    else setDraftJson(JSON.stringify(item.value, null, 2));
    setJsonError(null);
  }, [item.value, kind]);

  const dirty = (() => {
    if (kind === 'string') return draftString !== ((item.value as string) ?? '');
    if (kind === 'number') return draftNumber !== ((item.value as number) ?? 0);
    if (kind === 'boolean') return draftBoolean !== Boolean(item.value);
    return draftJson !== JSON.stringify(item.value, null, 2);
  })();

  const onSave = () => {
    let nextValue: unknown;
    if (kind === 'string') nextValue = draftString;
    else if (kind === 'number') nextValue = draftNumber;
    else if (kind === 'boolean') nextValue = draftBoolean;
    else {
      try {
        nextValue = JSON.parse(draftJson);
        setJsonError(null);
      } catch (e) {
        setJsonError(e instanceof Error ? e.message : 'invalid JSON');
        return;
      }
    }
    update.mutate({ key: item.key, value: nextValue });
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={`s-${item.key}`} className="font-mono text-xs">
          {item.key}
        </Label>
        <div className="flex items-center gap-1.5">
          {canReadAudit && (
            <Link
              to="/audit"
              search={{ resourceType: 'setting', resourceId: item.key }}
              className="inline-flex h-6 items-center gap-0.5 rounded px-1.5 text-[11px] text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              title="ดูประวัติการเปลี่ยนค่า"
              aria-label="ดู audit"
            >
              <Activity className="h-3 w-3" />
            </Link>
          )}
          {dirty && canEdit && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onSave}
              disabled={update.isPending}
            >
              <Save className="mr-1.5 h-3 w-3" />
              {update.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
            </Button>
          )}
        </div>
      </div>

      {item.description && <p className="text-[11px] text-slate-500">{item.description}</p>}

      {kind === 'string' && (
        <Input
          id={`s-${item.key}`}
          value={draftString}
          onChange={(e) => setDraftString(e.target.value)}
          disabled={!canEdit}
        />
      )}
      {kind === 'number' && (
        <Input
          id={`s-${item.key}`}
          type="number"
          value={draftNumber}
          onChange={(e) => setDraftNumber(Number(e.target.value))}
          disabled={!canEdit}
        />
      )}
      {kind === 'boolean' && (
        <label className="flex items-center gap-2 text-sm">
          <input
            id={`s-${item.key}`}
            type="checkbox"
            checked={draftBoolean}
            onChange={(e) => setDraftBoolean(e.target.checked)}
            disabled={!canEdit}
            className="h-4 w-4"
          />
          {draftBoolean ? 'เปิด' : 'ปิด'}
        </label>
      )}
      {kind === 'json' && (
        <>
          <Textarea
            id={`s-${item.key}`}
            value={draftJson}
            onChange={(e) => setDraftJson(e.target.value)}
            disabled={!canEdit}
            rows={4}
            className="font-mono text-xs"
          />
          {jsonError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>JSON ผิดพลาด: {jsonError}</AlertDescription>
            </Alert>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Specialized editor for `auth.required_2fa_roles`. Loads the live
 * roles list so the admin picks from real slugs instead of typing
 * JSON. Persists exactly the same shape (`string[]` of slugs) so the
 * backend reader (`isRoleRequired2fa`, `loadRequired2faRoleSlugs`)
 * doesn't need a special case.
 *
 * Tolerates a non-array stored value by treating it as `[]` for the
 * draft — matches the backend's fail-open posture on corrupted
 * settings. The admin saving from this widget will write a clean
 * array, healing the bad value naturally.
 */
function Required2faRolesRow({ item, canEdit, canReadAudit }: SettingRowProps) {
  const update = useUpdateSetting();
  const rolesQuery = useQuery(rolesListQueryOptions());
  const roles = rolesQuery.data ?? [];

  const initialSlugs = Array.isArray(item.value)
    ? (item.value as unknown[]).filter((x): x is string => typeof x === 'string')
    : [];
  const [draft, setDraft] = useState<string[]>(initialSlugs);

  // Sync draft when the remote value changes (e.g. someone else
  // saved on another tab — react-query refetched).
  useEffect(() => {
    const next = Array.isArray(item.value)
      ? (item.value as unknown[]).filter((x): x is string => typeof x === 'string')
      : [];
    setDraft(next);
  }, [item.value]);

  // Order-independent dirty check — the admin checking/unchecking in
  // any order shouldn't make the save button flicker.
  const dirty = (() => {
    if (draft.length !== initialSlugs.length) return true;
    const a = [...draft].sort();
    const b = [...initialSlugs].sort();
    return a.some((s, i) => s !== b[i]);
  })();

  const toggle = (slug: string) => {
    setDraft((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };

  const onSave = () => {
    // Persist as the same shape the backend reader expects — array of
    // slug strings, deduped + sorted for deterministic storage.
    const deduped = Array.from(new Set(draft)).sort();
    update.mutate({ key: item.key, value: deduped });
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={`s-${item.key}`} className="font-mono text-xs">
          {item.key}
        </Label>
        <div className="flex items-center gap-1.5">
          {canReadAudit && (
            <Link
              to="/audit"
              search={{ resourceType: 'setting', resourceId: item.key }}
              className="inline-flex h-6 items-center gap-0.5 rounded px-1.5 text-[11px] text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              title="ดูประวัติการเปลี่ยนค่า"
              aria-label="ดู audit"
            >
              <Activity className="h-3 w-3" />
            </Link>
          )}
          {dirty && canEdit && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onSave}
              disabled={update.isPending}
            >
              <Save className="mr-1.5 h-3 w-3" />
              {update.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
            </Button>
          )}
        </div>
      </div>

      {item.description && <p className="text-[11px] text-slate-500">{item.description}</p>}

      {rolesQuery.isLoading ? (
        <p className="text-xs text-slate-500">กำลังโหลด roles...</p>
      ) : roles.length === 0 ? (
        <p className="text-xs text-slate-500">ไม่มี role ในระบบ</p>
      ) : (
        <fieldset
          id={`s-${item.key}`}
          className="grid grid-cols-1 gap-1.5 rounded-md border border-slate-200 p-3 sm:grid-cols-2 dark:border-slate-700"
        >
          {roles.map((role) => {
            const checked = draft.includes(role.slug);
            return (
              <label
                key={role.id}
                className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!canEdit}
                  onChange={() => toggle(role.slug)}
                  className="h-4 w-4"
                />
                <span>{roleLabelTh(role.slug)}</span>
                <span className="font-mono text-[10px] text-slate-400">({role.slug})</span>
              </label>
            );
          })}
        </fieldset>
      )}
    </div>
  );
}
